import React from 'react'
import { Outlet } from 'react-router-dom'
import { useLocation } from 'react-router-dom'
import { AppSidebar } from '@/components/layout/AppSidebar'
import { PageTransition } from '@/components/layout/PageTransition'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Separator } from '@/components/ui/separator'
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar'
import { getBreadcrumbs, ROUTE_REGISTRY } from '@/lib/routes'
import { envConfig } from '@/lib/env-config'
import { HeaderProvider, useHeader } from '@/contexts/header.context'
import { useTheme } from '@/contexts/theme.context'
import { ActivationGate } from '@/components/common/ActivationGate'

function DashboardLayoutInner() {
  const location = useLocation()
  const breadcrumbs = getBreadcrumbs(location.pathname)
  const { headerExtra, headerRight } = useHeader()
  const { theme } = useTheme()

  // Check if current route needs relative header positioning
  const currentRoute = Object.values(ROUTE_REGISTRY).find(
    route => route.path === location.pathname,
  )
  const isRelativeHeader = currentRoute?.headerPosition === 'relative'

  return (
    <div className="w-full text-foreground antialiased">
      <SidebarProvider defaultOpen>
        <div
          className="flex min-h-screen min-w-screen border-t-4"
          style={{
            borderTopColor:
              theme === 'dark'
                ? envConfig.darkBorderColor
                : envConfig.borderColor,
          }}
        >
          <AppSidebar variant="inset" collapsible="icon" />
          <SidebarInset className="bg-background relative w-full flex-1 flex-col md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow-sm md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-0">
            <header
              className={`${isRelativeHeader ? 'relative' : 'absolute top-0 left-0 right-0'} z-10 flex h-16 shrink-0 items-center gap-2 backdrop-blur-lg backdrop-saturate-150 border-b border-gray-200/60 dark:border-border/60`}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-white/85 to-white/60 dark:from-background/95 dark:via-background/85 dark:to-background/60" />
              <div className="relative z-10 flex items-center gap-2 px-4 w-full">
                <SidebarTrigger className="-ml-1" />
                <Separator
                  orientation="vertical"
                  className="mr-2 data-[orientation=vertical]:h-4"
                />
                {headerExtra && (
                  <div className="flex-shrink-0 mr-2">{headerExtra}</div>
                )}
                <Breadcrumb>
                  <BreadcrumbList>
                    {breadcrumbs.map((crumb, index) => {
                      const isLast = index === breadcrumbs.length - 1
                      const isFirstLevel = index === 0
                      const isGrouping = isFirstLevel && breadcrumbs.length > 1

                      return (
                        <React.Fragment key={crumb.label + index}>
                          <BreadcrumbItem
                            className={isLast ? undefined : 'hidden md:block'}
                          >
                            {isLast || isGrouping ? (
                              <BreadcrumbPage
                                isCurrentPage={isLast}
                                className={isLast ? 'font-medium' : ''}
                              >
                                {crumb.label}
                              </BreadcrumbPage>
                            ) : (
                              <BreadcrumbLink to={crumb.href || '#'}>
                                {crumb.label}
                              </BreadcrumbLink>
                            )}
                          </BreadcrumbItem>
                          {!isLast && (
                            <BreadcrumbSeparator className="hidden md:block" />
                          )}
                        </React.Fragment>
                      )
                    })}
                  </BreadcrumbList>
                </Breadcrumb>
                {headerRight && (
                  <div className="ml-auto flex-shrink-0">{headerRight}</div>
                )}
              </div>
            </header>
            <div className="flex flex-1 flex-col h-full">
              <div className="flex flex-1 h-full overflow-y-auto">
                <PageTransition>
                  <Outlet />
                </PageTransition>
              </div>
            </div>
          </SidebarInset>
        </div>
      </SidebarProvider>
    </div>
  )
}

export function DashboardLayout() {
  return (
    <ActivationGate>
      <HeaderProvider>
        <DashboardLayoutInner />
      </HeaderProvider>
    </ActivationGate>
  )
}
